package com.eladfinish.bible.contest.screens.chapterComplete.usecases

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.common.settings.KeyValueSettings
import com.eladfinish.bible.contest.common.usecases.RemoveNikudAndTeamimAndMakafUseCase
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.KEY_ENABLE_LINE_LENGTH_ACCORDING_TO_WORD_LENGTH
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.KEY_ENABLE_SPACES_BETWEEN_BLANKS_IN_CHAPTER_COMPLETE
import com.eladfinish.bible.contest.constants.StringConstants.MAKAF
import com.eladfinish.bible.contest.text.ClickAction
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannedRange
import com.eladfinish.bible.contest.utils.RegexUtils.UNDERSCORES

class AppendBlankWithRevealSpanUseCase(
    private val settings: KeyValueSettings,
    private val removeNikudAndTeamimAndMakafUseCase: RemoveNikudAndTeamimAndMakafUseCase,
) {

    /**
     * Appends an underline "blank" and emits:
     * - CustomTypeface (for underscores style)
     * - ForegroundColor
     * - Clickable(RevealBlank)
     *
     * Returns whether the originWord ends with MAKAF (useful for callers).
     */
    operator fun invoke(
        out: StringBuilder,
        spans: MutableList<SpannedRange>,
        originWord: String,
        color: Color,
        typefaceFamily: String,
        appendSpaceAfterMakaf: Boolean,
    ): Boolean {
        val endsWithMakaf = originWord.endsWith(suffix = MAKAF.toString())

        val partOnlyLetters = removeNikudAndTeamimAndMakafUseCase(originWord)
        val underline = replaceWordWithUnderline(word = partOnlyLetters)

        val start = out.length
        out.append(underline)
        val end = out.length

        spans += SpannedRange(
            spanType = SpanType.CustomTypeface(
                family = typefaceFamily,
            ),
            textRange = TextRange(
                start = start,
                end = end,
            ),
        )

        spans += SpannedRange(
            spanType = SpanType.ForegroundColor(
                color = color,
            ),
            textRange = TextRange(
                start = start,
                end = end,
            ),
        )

        spans += SpannedRange(
            spanType = SpanType.Clickable(
                action = ClickAction.RevealBlank(
                    originWord = originWord,
                    endsWithMakaf = endsWithMakaf,
                ),
            ),
            textRange = TextRange(
                start = start,
                end = end,
            ),
        )

        if (appendSpaceAfterMakaf && endsWithMakaf) {
            out.append(" ")
        }

        return endsWithMakaf
    }

    /**
     * Same behavior as in BuildContentByExposureTypeDomainUseCase.replaceWordWithUnderline()
     */
    private fun replaceWordWithUnderline(
        word: String,
    ): String {
        val variableLength = settings.getBoolean(
            key = KEY_ENABLE_LINE_LENGTH_ACCORDING_TO_WORD_LENGTH,
            defaultValue = true,
        )
        val spaceBlanks = settings.getBoolean(
            key = KEY_ENABLE_SPACES_BETWEEN_BLANKS_IN_CHAPTER_COMPLETE,
            defaultValue = true,
        )

        val underscores = 4

        return if (spaceBlanks) {
            val underline = word.replace(
                regex = Regex("[^\\s$MAKAF]"),
                replacement = "_",
            )

            if (!variableLength) {
                val fixedBlanks = "_".repeat(underscores)
                underline.replace(
                    regex = UNDERSCORES.toRegex(),
                    replacement = fixedBlanks,
                )
            } else {
                underline
            }
        } else {
            if (variableLength) {
                "_".repeat(word.length)
            } else {
                val noSpaces = word.replace(
                    regex = Regex("[\\s$MAKAF]"),
                    replacement = "",
                )
                val numOfSpaces = word.length - noSpaces.length
                val numOfWords = numOfSpaces + 1
                val num = underscores * numOfWords + numOfSpaces
                "_".repeat(num)
            }
        }
    }
}
